import { useState, useEffect, useCallback, memo } from "react";
import Swal from "sweetalert2";
import AnimacionCarga from "../components/animacion_de_carga";
import BarraSuperior from "./barra_superior";
import BotonSOS from "./sos/boton_sos";
import { consultarSimple, actualizarMultiple } from "../../functions/crud";
import { CONFIG } from "../../functions/variables_globales";
import {
  minutosGratisDe,
  minutosFranjaDe,
  calcularCobro,
  ejemplosCobro,
} from "../../functions/cobro_parqueadero";

const DURACIONES = ["gratis", "franja"];

const CAMPOS = [
  "valorHoraCarro",
  "valorHoraMoto",
  "valorFranjaCarro",
  "valorFranjaMoto",
];

const formatearMoneda = (valor) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor || 0);

const val = (v) => (v === null || v === undefined ? "" : String(v));

// Duración editable como valor + unidad; se guarda siempre en minutos.
const separarDuracion = (minutos) => {
  const enHoras = minutos > 0 && minutos % 60 === 0;
  return { valor: String(enHoras ? minutos / 60 : minutos), unidad: enHoras ? "horas" : "minutos" };
};

const aMinutos = (valor, unidad) => {
  const numero = parseFloat(valor) || 0;
  return Math.round(unidad === "horas" ? numero * 60 : numero);
};

const Field = ({ label, value, onChange, error }) => (
  <div style={s.fieldWrap}>
    <label style={s.label}>{label}</label>
    <input
      style={{ ...s.input, borderColor: error ? "#ef4444" : "#e5e7eb" }}
      inputMode="decimal"
      value={value}
      placeholder={label}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && <span style={s.error}>{error}</span>}
  </div>
);

const CobroParqueaderos = memo(() => {
  const [ruta, setRuta] = useState(null);
  const [tarifas, setTarifas] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const nit = CONFIG.codigoCliente();
      const idConjunto = nit ? await consultarSimple(`clientes/${nit}`) : null;
      if (!idConjunto || typeof idConjunto !== "string") return;

      const rutaConjunto = `coordi_ph_clientes/${nit}/${idConjunto}`;
      const datos = (await consultarSimple(rutaConjunto)) || {};

      const inicial = {
        valorHoraCarro: datos.valorHoraCarro,
        valorHoraMoto: datos.valorHoraMoto,
        valorFranjaCarro: datos.valorFranjaCarro,
        valorFranjaMoto: datos.valorFranjaMoto,
      };

      // Antes valorHora* se usaba como valor de franja; se precarga para no perder la tarifa configurada.
      if (inicial.valorFranjaCarro == null && inicial.valorFranjaMoto == null) {
        inicial.valorFranjaCarro = inicial.valorHoraCarro;
        inicial.valorFranjaMoto = inicial.valorHoraMoto;
        inicial.valorHoraCarro = null;
        inicial.valorHoraMoto = null;
      }

      setRuta(rutaConjunto);
      const minutosGratis = minutosGratisDe(datos);
      const minutosFranja = minutosFranjaDe(datos);

      setTarifas({
        ...Object.fromEntries(CAMPOS.map((c) => [c, val(inicial[c])])),
        gratis: separarDuracion(minutosGratis),
        franja: separarDuracion(minutosFranja),
      });
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const update = (campo, valor) => setTarifas((prev) => ({ ...prev, [campo]: valor }));

  const updateDuracion = (campo, cambios) =>
    setTarifas((prev) => ({ ...prev, [campo]: { ...prev[campo], ...cambios } }));

  const errorDe = (campo) => {
    if (DURACIONES.includes(campo)) {
      const { valor, unidad } = tarifas[campo];
      const v = valor.trim();
      if (v === "") return null;
      if (isNaN(Number(v)) || Number(v) < 0) return "Número válido";
      if (unidad === "minutos" && !Number.isInteger(Number(v))) return "Minutos enteros";
      return null;
    }
    const v = (tarifas[campo] || "").trim();
    return v !== "" && (isNaN(Number(v)) || Number(v) < 0) ? "Ingrese solo números (0 si no se cobra)" : null;
  };

  const minutosDe = (campo) => aMinutos(tarifas[campo].valor, tarifas[campo].unidad);

  const guardar = async (e) => {
    e.preventDefault();
    if ([...CAMPOS, ...DURACIONES].some(errorDe)) return;
    if (minutosDe("franja") <= 0) {
      Swal.fire("Revise los datos", "La franja debe durar más de 0 minutos", "warning");
      return;
    }

    setGuardando(true);
    try {
      const cambios = {
        [`${ruta}/usuario`]: localStorage.getItem("NombreCompleto") || "",
        [`${ruta}/fecha`]: new Date().toISOString(),
        [`${ruta}/minutosGratisParqueadero`]: String(minutosDe("gratis")),
        [`${ruta}/minutosFranjaParqueadero`]: String(minutosDe("franja")),
      };
      CAMPOS.forEach((c) => {
        cambios[`${ruta}/${c}`] = (tarifas[c] || "").trim();
      });

      await actualizarMultiple(cambios);

      Swal.fire({
        title: "¡Guardado!",
        text: "Tarifas de parqueadero guardadas",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo guardar: " + err.message, "error");
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return <AnimacionCarga />;
  if (!ruta) return <div style={s.empty}>No se encontró información del conjunto.</div>;

  const campo = (nombre, label) => (
    <Field
      label={label}
      value={tarifas[nombre]}
      onChange={(v) => update(nombre, v)}
      error={errorDe(nombre)}
    />
  );

  const tarifaFormulario = (hora, franja) => ({
    minutosGratis: minutosDe("gratis"),
    minutosFranja: minutosDe("franja"),
    valorHora: isNaN(parseFloat(tarifas[hora])) ? null : parseFloat(tarifas[hora]),
    valorFranja: parseFloat(tarifas[franja]) || 0,
  });

  // Ejemplos calculados con los datos del formulario, aún sin guardar.
  const tablaEjemplos = () => {
    if (minutosDe("franja") <= 0) {
      return <p style={s.ayuda}>Defina la duración de la franja para ver ejemplos de cobro.</p>;
    }

    const carro = tarifaFormulario("valorHoraCarro", "valorFranjaCarro");
    const moto = tarifaFormulario("valorHoraMoto", "valorFranjaMoto");
    const ejemplos = ejemplosCobro(minutosDe("gratis"), minutosDe("franja"));

    return (
      <Section title="🧮 Ejemplos de cobro">
        <table style={s.tabla}>
          <thead>
            <tr>
              <th style={{ ...s.th, textAlign: "left" }}>Descripción</th>
              <th style={s.th}>Carro</th>
              <th style={s.th}>Moto</th>
            </tr>
          </thead>
          <tbody>
            {ejemplos.map((ejemplo) => (
              <tr key={ejemplo.descripcion}>
                <td style={{ ...s.td, textAlign: "left" }}>{ejemplo.descripcion}</td>
                <td style={s.td}>{formatearMoneda(calcularCobro(carro, ejemplo.minutos).total)}</td>
                <td style={s.td}>{formatearMoneda(calcularCobro(moto, ejemplo.minutos).total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    );
  };

  const duracion = (nombre) => {
    const error = errorDe(nombre);
    return (
      <div style={s.grid2}>
        <div style={s.fieldWrap}>
          <label style={s.label}>Valor</label>
          <input
            style={{ ...s.input, borderColor: error ? "#ef4444" : "#e5e7eb" }}
            inputMode="decimal"
            value={tarifas[nombre].valor}
            placeholder="Valor"
            onChange={(e) => updateDuracion(nombre, { valor: e.target.value })}
          />
          {error && <span style={s.error}>{error}</span>}
        </div>
        <div style={s.fieldWrap}>
          <label style={s.label}>Unidad</label>
          <select
            style={s.input}
            value={tarifas[nombre].unidad}
            onChange={(e) => updateDuracion(nombre, { unidad: e.target.value })}
          >
            <option value="minutos">Minutos</option>
            <option value="horas">Horas</option>
          </select>
        </div>
      </div>
    );
  };

  return (
    <div style={s.page}>
      <BarraSuperior />
      <br /><br /><br />

      <form onSubmit={guardar} style={s.form}>
        <h1 style={s.title}>Cobro parqueaderos</h1>

        {tablaEjemplos()}

        <Section title="⏱️ Tiempo gratuito">
          {duracion("gratis")}
        </Section>

        <Section title="📐 Duración de la franja">
          {duracion("franja")}
        </Section>

        <Section title="🕐 Tarifa por hora">
          <div style={s.grid2}>
            {campo("valorHoraCarro", "Valor por hora - Carro")}
            {campo("valorHoraMoto", "Valor por hora - Moto")}
          </div>
        </Section>

        <Section title="📅 Tarifa por franja">
          <div style={s.grid2}>
            {campo("valorFranjaCarro", "Valor franja - Carro")}
            {campo("valorFranjaMoto", "Valor franja - Moto")}
          </div>
        </Section>

        <button type="submit" disabled={guardando} style={{ ...s.btn, opacity: guardando ? 0.7 : 1 }}>
          {guardando ? "Guardando…" : "💾 Guardar tarifas"}
        </button>
      </form>
      <BotonSOS />
    </div>
  );
});

const Section = ({ title, children }) => (
  <div style={s.section}>
    <h3 style={s.sectionTitle}>{title}</h3>
    <div style={s.sectionBody}>{children}</div>
  </div>
);

const s = {
  page: {
    minHeight: "100vh",
    background: "#ffffff",
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    paddingBottom: 60,
  },
  form: { maxWidth: 960, margin: "0 auto", padding: "24px 20px" },
  title: { margin: "0 0 20px", fontSize: 22, fontWeight: 700, color: "#1a1a2e" },
  section: {
    background: "#fff", borderRadius: 16,
    boxShadow: "0 2px 16px rgba(37,99,235,0.07)",
    marginBottom: 20, overflow: "hidden",
  },
  sectionTitle: {
    margin: 0, padding: "16px 24px",
    fontSize: 14, fontWeight: 700, color: "#1a1a2e",
    borderBottom: "1px solid #f0f4ff",
    background: "linear-gradient(90deg,#f8faff,#fff)",
    letterSpacing: 0.3,
  },
  sectionBody: { padding: "20px 24px" },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px,1fr))",
    gap: 16,
  },
  fieldWrap: { display: "flex", flexDirection: "column", gap: 5 },
  label: {
    fontSize: 11, fontWeight: 700, color: "#6b7280",
    textTransform: "uppercase", letterSpacing: 0.5,
  },
  input: {
    padding: "10px 13px", border: "1.5px solid #e5e7eb",
    borderRadius: 10, fontSize: 14, outline: "none",
    boxSizing: "border-box", width: "100%",
  },
  error: { fontSize: 12, color: "#ef4444" },
  ayuda: { fontSize: 13, color: "#6b7280", textAlign: "center", margin: "0 0 16px" },
  btn: {
    width: "100%", padding: "15px",
    background: "linear-gradient(135deg, #c0392b, #ef4444)",
    color: "#fff", border: "none", borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: "pointer",
    boxShadow: "0 4px 20px rgba(239,68,68,0.3)",
  },
  empty: { textAlign: "center", padding: 60, color: "#9ca3af", fontSize: 16 },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    padding: "8px 10px", textAlign: "right", fontWeight: 700,
    background: "#f3f4f6", border: "1px solid #e5e7eb", color: "#1a1a2e",
  },
  td: { padding: "8px 10px", textAlign: "right", border: "1px solid #e5e7eb", color: "#374151" },
};

export default CobroParqueaderos;
